import { RefreshCw, Truck } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import rideScheduleCache from '../../services/rideScheduleCache';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import leaveApi from '../LeaveManagement/leaveApi';
import { getSiteDetails } from '../SiteManagement/siteApi';
import { IconTextButton, SearchBar } from '../ui';
import AssignConvo from './Assign_Convo';
import CreateRideSchedule from './Create_Ride_Schedule';
import RideDetails from './RideDetails';
import RideScheduleCard from './RideScheduleCard';

const ScheduleARide = () => {
  // Log screen view
  useScreenLogging('ScheduleARide', {
    component: 'ScheduleARide',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [rideSchedules, setRideSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRide, setSelectedRide] = useState(null);
  
  // Check URL params to determine if we should show details or create ride
  const viewParam = searchParams.get('view');
  const rideIdParam = searchParams.get('rideId');
  const showDetails = viewParam === 'ride-details' && rideIdParam;
  const showCreateRide = viewParam === 'create-ride';
  const showAssignConvoy = viewParam === 'assign-convoy';

  // Load selected ride when rideId is in URL
  useEffect(() => {
    if (showDetails && rideIdParam && rideSchedules.length > 0) {
      const ride = rideSchedules.find(r => r.ride_id === rideIdParam);
      if (ride) {
        setSelectedRide(ride);
      } else if (!isLoading) {
        // If ride not found and not loading, go back to list
        setSearchParams({});
      }
    }
  }, [showDetails, rideIdParam, rideSchedules, isLoading, setSearchParams]);

  // Fetch ride schedules on component mount
  const fetchRideSchedules = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedData = rideScheduleCache.get();
        if (cachedData) {
          console.log('📦 Using cached ride schedules');
          setRideSchedules(cachedData);
          setFilteredSchedules(cachedData);
          setIsLoading(false);
          return;
        }
      } else {
        // Clear cache on force refresh
        rideScheduleCache.clear();
      }

      // Fetch future schedules
      const schedules = await driverApi.fetchFutureDriverSchedules();
      
      // Enrich schedules with site and supervisor details
      const enrichedSchedules = await Promise.all(
        schedules.map(async (schedule) => {
          try {
            // Fetch site details
            const fromSiteDetails = await getSiteDetails(schedule.from_site_id).catch(() => null);
            const toSiteDetails = await getSiteDetails(schedule.to_site_id).catch(() => null);
            
            // Fetch supervisor details if available using leaveApi.getUserInfo
            let fromSupervisorName = 'Unknown';
            let toSupervisorName = 'Unknown';
            
            if (schedule.from_supervisor_id) {
              try {
                const fromSupervisorDetails = await leaveApi.getUserInfo(schedule.from_supervisor_id);
                fromSupervisorName = fromSupervisorDetails?.user_name || fromSupervisorDetails?.name || 'Unknown';
              } catch (error) {
                console.error('Error fetching from supervisor details:', error);
              }
            }
            
            if (schedule.to_supervisor_id) {
              try {
                const toSupervisorDetails = await leaveApi.getUserInfo(schedule.to_supervisor_id);
                toSupervisorName = toSupervisorDetails?.user_name || toSupervisorDetails?.name || 'Unknown';
              } catch (error) {
                console.error('Error fetching to supervisor details:', error);
              }
            }
            
            return {
              ...schedule,
              fromSiteName: fromSiteDetails?.site_name || 'Unknown',
              toSiteName: toSiteDetails?.site_name || 'Unknown',
              fromSupervisorName,
              toSupervisorName,
            };
          } catch (error) {
            console.error('Error enriching schedule:', error);
            return {
              ...schedule,
              fromSiteName: 'Unknown',
              toSiteName: 'Unknown',
              fromSupervisorName: 'Unknown',
              toSupervisorName: 'Unknown',
            };
          }
        })
      );
      
      // Cache the enriched schedules
      rideScheduleCache.set(enrichedSchedules);
      
      // Log API event for viewing ride schedules
      loggingService.logInfo(
        'ride_schedule_view',
        `User viewed ride schedules (${enrichedSchedules.length} schedules)`,
        {
          endpoint: '/driver/schedules/future',
          method: 'GET',
          scheduleCount: enrichedSchedules.length,
          forceRefresh: forceRefresh,
        }
      ).catch(logError => {
        console.warn('[ScheduleARide] Failed to log event:', logError);
      });
      
      setRideSchedules(enrichedSchedules);
      setFilteredSchedules(enrichedSchedules);
    } catch (error) {
      console.error('Error fetching ride schedules:', error);
      setRideSchedules([]);
      setFilteredSchedules([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRideSchedules();
  }, [fetchRideSchedules]);

  // Clear cache when returning from create-ride or assign-convoy views
  // (a new ride might have been created)
  useEffect(() => {
    // If we're on the main list view and we just came from create/assign views
    // we should refresh to get the latest data
    if (!showCreateRide && !showAssignConvoy && !showDetails) {
      // Check if we should invalidate cache (e.g., after successful creation)
      // We'll check localStorage for a flag or just refresh on mount
      const shouldRefresh = sessionStorage.getItem('shouldRefreshRideSchedules');
      if (shouldRefresh === 'true') {
        sessionStorage.removeItem('shouldRefreshRideSchedules');
        fetchRideSchedules(true); // Force refresh
      }
    }
  }, [showCreateRide, showAssignConvoy, showDetails, fetchRideSchedules]);

  // Handle search functionality
  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);

    // Filter schedules based on search term
    if (value.length > 0) {
      const filtered = rideSchedules.filter(ride => {
        const searchLower = value.toLowerCase();
        return (
          ride.ride_id?.toLowerCase().includes(searchLower) ||
          ride.fromSiteName?.toLowerCase().includes(searchLower) ||
          ride.toSiteName?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredSchedules(filtered);
      
      // Generate suggestions
      const suggestions = filtered.slice(0, 5).map(ride => 
        `${ride.fromSiteName} to ${ride.toSiteName}`
      );
      setSearchSuggestions(suggestions);
    } else {
      setFilteredSchedules(rideSchedules);
      setSearchSuggestions([]);
    }
  };

  const handleSearchSubmit = value => {
    console.log('Search submitted:', value);
    // Search is already handled in handleSearchChange
  };

  const handleSuggestionClick = suggestion => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    // Filter is already applied in handleSearchChange
  };

  const handleRideClick = (ride) => {
    setSelectedRide(ride);
    // Update URL to show ride details
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('view', 'ride-details');
    newSearchParams.set('rideId', ride.ride_id);
    setSearchParams(newSearchParams);
  };

  const handleBackFromDetails = () => {
    setSelectedRide(null);
    // Remove query params to go back to list view
    setSearchParams({});
  };

  const handleCreateRide = () => {
    // Navigate to create ride schedule page
    setSearchParams({ view: 'create-ride' });
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  if (showDetails && selectedRide) {
    return (
      <RideDetails 
        ride={selectedRide} 
        onBack={handleBackFromDetails}
      />
    );
  }

  if (showCreateRide) {
    return <CreateRideSchedule />;
  }

  if (showAssignConvoy) {
    return <AssignConvo />;
  }

  return (
    <div className="w-full h-full flex flex-col
    ">
      {/* Header Section */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h2 className={typography.h2} style={{ color: '#000000' }}>
            Schedule a Ride
          </h2>
          <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
            View and manage scheduled rides
          </p>
        </div>
        {/* Create Ride Button */}
      <div className="mt-auto pt-4">
        <IconTextButton
          icon={<Truck className="w-6 h-6 text-white" />}
          text="Create Ride Schedule"
          onClick={handleCreateRide}
          className="w-full"
          size="lg"
        />
      </div>
      </div>

      {/* Search Bar Section */}
      <div className="mb-6">
        <SearchBar
          placeholder="Search by from site, or to site..."
          value={searchTerm}
          onChange={handleSearchChange}
          onSearch={handleSearchSubmit}
          suggestions={searchSuggestions}
          onSuggestionClick={handleSuggestionClick}
          className="w-full max-w-lg"
        />
      </div>

      {/* Scheduled Rides Section */}
      <div className="mb-6 flex justify-between items-center">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Scheduled Rides
        </h3>
        <button
          onClick={() => fetchRideSchedules(true)}
          disabled={isLoading}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Rides List */}
      <div className="flex-1 overflow-y-auto mb-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading ride schedules...</div>
          </div>
        ) : filteredSchedules.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500 text-center">
              <p className="text-lg mb-2">No rides found</p>
              <p className="text-sm">Try adjusting your search or create a new ride</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSchedules.map((ride) => (
              <RideScheduleCard
                key={ride.ride_id}
                ride={ride}
                onClick={() => handleRideClick(ride)}
                formatTimestamp={formatTimestamp}
              />
            ))}
          </div>
        )}
      </div> 
    </div>
  );
};

export default ScheduleARide;

